import { useMemo } from "react";
import type { BaseEntity } from "../../../UniversalCRMTable/types";
import type { UniversalEntityPageContextValue } from "../../contexts/UniversalEntityPageContext";
import { useCompositionContextSlices } from "./useCompositionContextSlices";
import type { CompositionContextValueParams } from "./compositionContextValueTypes";

export type {
  CompositionNavigation,
  CompositionDataStateInput,
  CompositionHandlersInput,
  CompositionContextValueParams,
} from "./compositionContextValueTypes";

export type { CompositionContextSlices } from "./useCompositionContextSlices";
export { useCompositionContextSlices } from "./useCompositionContextSlices";

/**
 * Assembles the compatibility (god) context value out of the independently
 * memoized slices from `useCompositionContextSlices`.
 *
 * This used to be ONE `useMemo` with an 85-entry dependency array, which meant
 * every sub-object was re-allocated whenever any single dependency moved — a
 * keystroke in the search box invalidated `pageConfig`, `handlers`, `dataState`
 * and everything else, so the entire page tree re-rendered. The slices are now
 * memoized on their own inputs and this memo just composes them, so a consumer
 * reading one slice (via the narrow contexts) is unaffected by the others.
 */
export function useCompositionContextValue<T extends BaseEntity>(
  params: CompositionContextValueParams<T>
): UniversalEntityPageContextValue<T> {
  const slices = useCompositionContextSlices<T>(params);

  const {
    pageConfig,
    auth,
    navigation,
    dataState,
    entityState,
    filterState,
    exportState,
    handlers,
    callbacks,
    mutations,
    viewModeState,
    infiniteScrollState,
  } = slices;

  const { bulkActions, bulkActionMutations, savedViewsState, paginationModeState } = params;

  return useMemo(
    () => ({
      pageConfig,
      auth,
      navigation,
      dataState,
      entityState,
      filterState,
      exportState,
      handlers,
      callbacks,
      mutations,
      bulkActions,
      bulkActionMutations,
      savedViewsState,
      viewModeState,
      infiniteScrollState,
      paginationModeState,
    }),
    [
      pageConfig,
      auth,
      navigation,
      dataState,
      entityState,
      filterState,
      exportState,
      handlers,
      callbacks,
      mutations,
      bulkActions,
      bulkActionMutations,
      savedViewsState,
      viewModeState,
      infiniteScrollState,
      paginationModeState,
    ]
  );
}
